/**
 * デバッグ用「全ステージ解放」フラグ。
 *
 * 動作確認のために解放条件を無視して全ステージを遊べるようにする。
 * **判定ロジックはこのモジュールに集約**する(タイピングの `logic/unlock.ts` と
 * パズルの `services/puzzleProgress.ts` は、ここの `isDebugUnlockAll()` だけを見る)。
 *
 * - 発動: URL に `?debugUnlock=1` → localStorage に保存して次のセッションでも有効
 * - 解除: URL に `?debugUnlock=0`、または画面のバッジをクリック
 * - **進捗データ(星・クリア状態・`paltyping.progress` 等)には一切触らない**。
 *   フラグを消せば元の解放状態にそのまま戻る(非破壊)。
 *
 * TODO(将来): Firebase Auth の admin カスタムクレーム(`request.auth.token.admin`)へ
 * 移行する。URL パラメータは誰でも付けられるので、サーバー側の権限が必要になる機能
 * (コイン付与・進捗の巻き戻し等)をデバッグメニューに足すときは、この localStorage
 * ではなく ID トークンのクレームを見ること。いまはクライアント表示だけを変える用途に限る
 * (未解放ステージをクリアしても Functions の検証は今までどおり効く)。
 */

import type { KeyValueStorage } from './progress';

/** localStorage のキー。 */
export const DEBUG_UNLOCK_STORAGE_KEY = 'paltyping.debug.unlockAll';

/** URL のクエリパラメータ名。 */
export const DEBUG_UNLOCK_PARAM = 'debugUnlock';

/** 保存される値(`'1'` 以外はすべて無効扱い)。 */
const ENABLED_VALUE = '1';

/**
 * URL の `?debugUnlock=` を読む(純関数)。
 *
 * - `1` / `true` / `on` → `true`(有効化)
 * - `0` / `false` / `off` / 空文字 → `false`(解除)
 * - 指定なし → `null`(現状維持)
 */
export function readDebugUnlockParam(search: string): boolean | null {
  const match = new RegExp(`[?&]${DEBUG_UNLOCK_PARAM}=([^&#]*)`).exec(search);
  if (!match) {
    // `?debugUnlock`(値なし)は有効化として扱う。
    return new RegExp(`[?&]${DEBUG_UNLOCK_PARAM}(?:[&#]|$)`).test(search) ? true : null;
  }
  const value = decodeURIComponent(match[1] ?? '').toLowerCase();
  if (value === '1' || value === 'true' || value === 'on') return true;
  return false;
}

/** 変更通知(バッジの表示・非表示を即時に切り替えるため)。 */
type Listener = () => void;
const listeners = new Set<Listener>();

/** 読み取りのたびに localStorage を叩かないためのキャッシュ。 */
let cached: boolean | null = null;

function safeLocalStorage(): KeyValueStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function readStored(storage: KeyValueStorage | null): boolean {
  try {
    return storage?.getItem(DEBUG_UNLOCK_STORAGE_KEY) === ENABLED_VALUE;
  } catch {
    return false;
  }
}

function writeStored(storage: KeyValueStorage | null, enabled: boolean): void {
  try {
    if (enabled) storage?.setItem(DEBUG_UNLOCK_STORAGE_KEY, ENABLED_VALUE);
    else storage?.removeItem(DEBUG_UNLOCK_STORAGE_KEY);
  } catch {
    // プライベートモード・容量超過。その場かぎりのデバッグとして続行する。
  }
}

function emit(): void {
  for (const listener of [...listeners]) listener();
}

/**
 * URL パラメータを保存に反映してから、現在の状態を返す。
 *
 * アプリ起動時に1回呼ぶ(`main.tsx`)。テストからは `search` / `storage` を注入する。
 */
export function initDebugUnlock(
  search: string = typeof location === 'undefined' ? '' : location.search,
  storage: KeyValueStorage | null = safeLocalStorage(),
): boolean {
  const fromUrl = readDebugUnlockParam(search);
  if (fromUrl !== null) writeStored(storage, fromUrl);
  const next = fromUrl ?? readStored(storage);
  const changed = cached !== next;
  cached = next;
  if (changed) emit();
  return next;
}

/**
 * 全ステージ解放デバッグが有効か。
 *
 * 初回呼び出しで URL と localStorage を読む(`initDebugUnlock` を呼び忘れても効く)。
 */
export function isDebugUnlockAll(): boolean {
  if (cached === null) return initDebugUnlock();
  return cached;
}

/** 手動で切り替える(バッジのクリック=解除)。進捗データには触らない。 */
export function setDebugUnlockAll(
  enabled: boolean,
  storage: KeyValueStorage | null = safeLocalStorage(),
): void {
  writeStored(storage, enabled);
  if (cached === enabled) return;
  cached = enabled;
  emit();
}

/** 変更購読(React の `useSyncExternalStore` 用)。 */
export function subscribeDebugUnlock(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** テスト用: キャッシュを捨てて未初期化に戻す(保存値は消さない)。 */
export function resetDebugUnlockCache(): void {
  cached = null;
}
